import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { formatUSD } from "@/lib/utils";
import { CheckCircle2, Clock, XCircle, Loader2, ArrowRight, RefreshCw } from "lucide-react";

// We deliberately use a raw axios call (not the authenticated `api` client)
// because this page is PUBLIC — the user lands here directly from Payku
// without a guaranteed session. The backend public-status endpoint
// re-verifies the payment with Payku before returning anything, so the
// order_id (a UUID) is the only credential we need.
const PUBLIC_API = process.env.REACT_APP_BACKEND_URL;

function formatCLP(amount) {
  try {
    const formatted = new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 }).format(amount);
    return `CLP $${formatted}`;
  } catch {
    return `CLP $${Number(amount).toLocaleString()}`;
  }
}

export default function PaykuReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const order = params.get("order");
  const [topup, setTopup] = useState(null);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(
        `${PUBLIC_API}/api/payments/payku/public-status`,
        { params: { order } },
      );
      setTopup(data);
      return data;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load the transaction");
      return null;
    }
  };

  const forceSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      // public-status itself re-verifies on every call
      await fetchStatus();
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (!order) {
      setError("Missing order id in the URL.");
      return;
    }
    fetchStatus();
    // eslint-disable-next-line
  }, [order]);

  // Auto-poll while pending — public-status re-verifies on each call, so
  // any moment Payku reports success the wallet is credited.
  useEffect(() => {
    if (!topup) return;
    const final = topup.credited || ["SUCCESS", "FAILED"].includes(topup.status);
    if (final || pollCount >= 20) return;
    const t = setTimeout(async () => {
      await fetchStatus();
      setPollCount((n) => n + 1);
    }, 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [topup, pollCount]);

  const isCredited = topup?.credited === true || topup?.status === "SUCCESS";
  const isFailed = topup?.status === "FAILED";
  const isPending = topup && !isCredited && !isFailed;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-sm p-8" data-testid="payku-return">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Payku payment</div>
        {!topup && !error && (
          <div className="text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your transaction…
          </div>
        )}
        {error && (
          <div className="rounded-lg border-2 border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="payku-return-error">
            <div className="font-bold mb-1">We couldn't load this transaction.</div>
            <div className="text-xs text-rose-700">{error}</div>
            {order && (
              <div className="text-xs text-rose-600 mt-2 font-mono">order ref: {order}</div>
            )}
          </div>
        )}
        {topup && (
          <>
            {isCredited && (
              <div className="space-y-1" data-testid="payku-return-success">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="w-7 h-7" />
                  <h1 className="text-2xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    Payment approved
                  </h1>
                </div>
                <p className="text-slate-600 text-sm">
                  Your wallet has been credited with <span className="font-bold text-slate-900">{formatUSD(topup.amount_usd)}</span>.
                </p>
              </div>
            )}
            {isFailed && (
              <div className="space-y-1" data-testid="payku-return-failed">
                <div className="flex items-center gap-2 text-rose-700">
                  <XCircle className="w-7 h-7" />
                  <h1 className="text-2xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    Payment failed
                  </h1>
                </div>
                <p className="text-slate-600 text-sm">
                  No funds were debited from your card and your wallet was not credited. You can try again from Make Payment.
                </p>
              </div>
            )}
            {isPending && (
              <div className="space-y-1" data-testid="payku-return-pending">
                <div className="flex items-center gap-2 text-amber-700">
                  <Clock className="w-7 h-7" />
                  <h1 className="text-2xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    Confirming your payment…
                  </h1>
                </div>
                <p className="text-slate-600 text-sm">
                  We're waiting for Payku's confirmation. This usually takes just a few seconds.
                </p>
              </div>
            )}

            <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">USD credit</div>
                <div className="font-bold text-slate-900">{formatUSD(topup.amount_usd)}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Paid</div>
                <div className="font-bold text-slate-900">{formatCLP(topup.local_amount)}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Status</div>
                <div className="font-bold text-slate-900">{topup.credited ? "CREDITED" : topup.status}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Payku reference</div>
                <div className="font-mono text-[11px] text-slate-700 truncate" title={topup.payku_token}>
                  {topup.payku_token || topup.order_id}
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="payku-return-dashboard"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#0F62FE] text-white text-sm font-bold hover:bg-[#0050E6]"
              >
                Go to dashboard <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate("/make-payment")}
                data-testid="payku-return-back"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
              >
                Back to Make Payment
              </button>
              {!isCredited && (
                <button
                  onClick={forceSync}
                  disabled={syncing}
                  data-testid="payku-return-sync"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                >
                  {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Check again
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
